import {
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
  AuthenticationDetails,
} from "amazon-cognito-identity-js";

const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;

if (!userPoolId || !clientId) {
  console.warn("Cognito user pool config missing — set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID in .env.local");
}

export const userPool = new CognitoUserPool({
  UserPoolId: userPoolId ?? "",
  ClientId: clientId ?? "",
});

export async function signIn(email: string, password: string): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool });
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(session),
        onFailure: (err) => reject(err),
        // New-password challenges are out of scope for this MVP.
        newPasswordRequired: () =>
          reject(new Error("Account requires a new password — set it in the AWS console first.")),
      },
    );
  });
}

export function signOut(): void {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

export async function currentSession(): Promise<CognitoUserSession | null> {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session);
    });
  });
}

export async function getIdToken(): Promise<string | null> {
  const session = await currentSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

/** Email claim from the current Cognito ID token, or null if not logged in.
 *  Used purely for client-side UI conditioning (e.g. showing an admin
 *  button). The SERVER must re-verify the JWT claim before doing anything
 *  privileged — never trust this value alone. */
export async function getCurrentUserEmail(): Promise<string | null> {
  const session = await currentSession();
  if (!session) return null;
  const payload = session.getIdToken().decodePayload();
  const email = (payload as { email?: unknown }).email;
  return typeof email === "string" ? email : null;
}
